import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { MantineProvider, createTheme } from '@mantine/core';
import { ModalsProvider } from '@mantine/modals';
import { Notifications } from '@mantine/notifications';
import { QueryClientProvider } from '@tanstack/react-query';
import { RouterProvider } from '@tanstack/react-router';
import { createTRPCOptionsProxy } from '@trpc/tanstack-react-query';
import type { AppRouter } from '@seo/api/src/routers';

import '@fontsource/ibm-plex-sans/400.css';
import '@fontsource/ibm-plex-sans/500.css';
import '@fontsource/ibm-plex-sans/600.css';
import '@fontsource/ibm-plex-sans/700.css';
import '@fontsource/ibm-plex-mono/400.css';
import '@fontsource/ibm-plex-mono/500.css';
import '@fontsource/ibm-plex-mono/600.css';
import '@fontsource/ibm-plex-mono/700.css';
import '@mantine/core/styles.css';
import '@mantine/notifications/styles.css';
import '@mantine/dates/styles.css';
import '@mantine/dropzone/styles.css';
import '@mantine/charts/styles.css';
import './styles/global.css';

import { cssVariablesResolver, theme } from './theme';
import { createAppRouter } from './router';
import { TRPCProvider, createClient, createQueryClient } from './lib/trpc';
import { ErrorBoundary, initWebSentry } from './sentry';

initWebSentry();

const queryClient = createQueryClient();
const trpcClient = createClient();
const trpcProxy = createTRPCOptionsProxy<AppRouter>({ client: trpcClient, queryClient });
const router = createAppRouter({ queryClient, trpc: trpcProxy });

const container = document.getElementById('root');
if (!container) throw new Error('#root is missing from index.html');

createRoot(container).render(
  <StrictMode>
    <MantineProvider
      theme={createTheme(theme)}
      cssVariablesResolver={cssVariablesResolver}
      defaultColorScheme="dark"
    >
      <ErrorBoundary>
        <QueryClientProvider client={queryClient}>
          <TRPCProvider trpcClient={trpcClient} queryClient={queryClient}>
            <ModalsProvider>
              <Notifications position="top-right" limit={4} />
              <RouterProvider router={router} />
            </ModalsProvider>
          </TRPCProvider>
        </QueryClientProvider>
      </ErrorBoundary>
    </MantineProvider>
  </StrictMode>,
);
